import React from "react";
import { Link } from "react-router-dom";
import { EditOutlined } from "@ant-design/icons";
import { getCountryName } from "../../constants/countries";

interface DataSummaryCardProps {
    brandName: string;
    periodDescription: string;
    cohortNames: string[];
    countryNames: string[];
    totalResponses?: number;
    calculatedAt?: string;
    changeFiltersLink: string;
}

export const DataSummaryCard: React.FC<DataSummaryCardProps> = ({
    brandName,
    periodDescription,
    cohortNames,
    countryNames,
    totalResponses,
    calculatedAt,
    changeFiltersLink
}) => {
    return (
        <div style={{
            marginBottom: '24px',
            padding: '12px 16px',
            background: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            fontSize: '14px',
            color: '#6b7280',
        }}>
            <strong style={{ color: '#374151' }}>Data:</strong> {brandName} • {periodDescription}
            {countryNames.length > 0 && (
                <span> • {countryNames.length === 1 ? 'Country' : 'Countries'}: {countryNames.join(', ')}</span>
            )}
            {cohortNames.length > 0 && (
                <span> • {cohortNames.length === 1 ? 'Cohort' : 'Cohorts'}: {cohortNames.join(', ')}</span>
            )}
            {totalResponses !== undefined && (
                <span> • {totalResponses} {totalResponses === 1 ? 'response' : 'responses'}</span>
            )}
            {calculatedAt && (
                <span> • Calculated: {new Date(calculatedAt).toLocaleString()}</span>
            )}
            {' • '}
            <Link to={changeFiltersLink} style={{ color: '#2563eb', textDecoration: 'none' }}>
                <EditOutlined /> Change filters
            </Link>
        </div>
    );
};

interface FilterInfo {
    brandId?: number;
    periodType?: 'last30' | 'last60' | 'last90' | 'last120' | 'last180' | 'last365' | 'allTime' | 'custom';
    dateFrom?: string;
    dateTo?: string;
    cohortIds?: number[];
    countryCodes?: string[];
}

interface Brand {
    id: number;
    name: string;
}

interface Cohort {
    id: number;
    displayName: string;
}

interface UseDataSummaryParams {
    filterInfo: FilterInfo | null;
    brandsData?: Brand[];
    cohortsData?: Cohort[];
}

/**
 * Hook to extract data summary information from filter data
 */
export const useDataSummary = ({ filterInfo, brandsData, cohortsData }: UseDataSummaryParams) => {
    const getBrandName = () => {
        if (!filterInfo?.brandId || !brandsData) return 'Unknown Brand';
        const brand = brandsData.find(b => b.id === filterInfo.brandId);
        return brand?.name || 'Unknown Brand';
    };

    const getPeriodDescription = () => {
        if (!filterInfo) return '';

        switch (filterInfo.periodType) {
            case 'last30': return 'Last 30 days';
            case 'last60': return 'Last 60 days';
            case 'last90': return 'Last 90 days';
            case 'last120': return 'Last 120 days';
            case 'last180': return 'Last 180 days';
            case 'last365': return 'Last 365 days';
            case 'allTime': return 'All time';
            case 'custom':
                if (filterInfo.dateFrom && filterInfo.dateTo) {
                    return `${new Date(filterInfo.dateFrom).toLocaleDateString()} - ${new Date(filterInfo.dateTo).toLocaleDateString()}`;
                }
                return 'Custom date range';
            default: return 'Last 30 days';
        }
    };

    const getCohortNames = () => {
        if (!filterInfo?.cohortIds || !cohortsData) return [];
        return filterInfo.cohortIds
            .map((id: number) => cohortsData.find(c => c.id === id)?.displayName)
            .filter(Boolean) as string[];
    };

    const getCountryNames = () => {
        if (!filterInfo?.countryCodes) return [];
        return filterInfo.countryCodes
            .map((code: string) => getCountryName(code))
            .filter(Boolean) as string[];
    };

    return {
        brandName: getBrandName(),
        periodDescription: getPeriodDescription(),
        cohortNames: getCohortNames(),
        countryNames: getCountryNames()
    };
};
